import {ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked} from '@angular/core';
import {Store} from '@ngrx/store';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {updateProject} from '../../service/project.actions';
import {selectSelectedProject} from '../../service/project.selectors';
import {ProjectDTO} from '../../dto/project';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';

import {MatButtonModule} from '@angular/material/button';
import {KeywordListComponent} from '../../../research/components/keyword-list/keyword-list.component';
import {
  ScreeningCriteriasListComponent
} from '../../../research/components/screening-criterias-list/screening-criterias-list.component';
import {ActivatedRoute, Router} from '@angular/router';
import {selectResearchKeywordState} from '../../../research/services/research-keyword/research-keyword.selectors';
import {selectScreeningCriteriaState} from '../../../research/services/screening-criteria/screening-criteria.selectors';
import {ProjectPermissionService} from '../../../../utils/services/project-permission.service';
import {MatTooltip} from '@angular/material/tooltip';
import {SaveBtnComponent} from '../../../../utils/components/save-btn/save-btn.component';
import {HeaderComponent} from '../../../../utils/components/header/header.component';

@Component({
  selector: 'app-project-detail',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    KeywordListComponent,
    ScreeningCriteriasListComponent,
    MatTooltip,
    SaveBtnComponent,
    HeaderComponent
  ],
  templateUrl: './project-detail.component.html',
  styleUrl: './project-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectDetailComponent {
  private readonly store: Store = inject(Store);
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly projectPermissionService: ProjectPermissionService = inject(ProjectPermissionService);

  private readonly keywordsState = this.store.selectSignal(selectResearchKeywordState);
  private readonly criteriaState = this.store.selectSignal(selectScreeningCriteriaState);
  private readonly currentProjectId = signal<number | undefined>(undefined);

  readonly project = this.store.selectSignal(selectSelectedProject);
  readonly showForm = signal(false);

  readonly form = this.fb.nonNullable.group({
    name: ['', Validators.required],
    topic: ['', Validators.required],
  });

  readonly canWriteProject = computed(() =>
    this.projectPermissionService.hasWritePermission(this.project() ?? undefined)
  );

  readonly ready = computed(() => {
    const projectId = this.project()?.id;
    if (!projectId) {
      return false;
    }

    const keywords = this.keywordsState().keywordsByProject[projectId] ?? [];
    const criteria = this.criteriaState().criteriaByProject[projectId] ?? [];
    return keywords.length > 0 && criteria.length > 0;
  });

  constructor() {
    effect(() => {
      const project = this.project();
      const canWrite = this.canWriteProject();

      if (!project) {
        this.form.reset();
        this.form.disable({emitEvent: false});
        this.currentProjectId.set(undefined);
        this.showForm.set(false);
        return;
      }

      const previousProjectId = untracked(() => this.currentProjectId());
      this.currentProjectId.set(project.id);

      this.patchForm(project);

      if (canWrite) {
        this.form.enable({emitEvent: false});
      } else {
        this.form.disable({emitEvent: false});
        this.showForm.set(false);
      }

      if (previousProjectId !== project.id) {
        this.showForm.set(false);
      }
    });
  }

  save(): void {
    const project = this.project();
    if (this.form.valid && project) {
      const toUpdate: ProjectDTO = {...project, ...this.form.getRawValue()};
      this.store.dispatch(updateProject({id: project.id, project: toUpdate}));
    }
  }

  toggleEdit(): void {
    if (!this.canWriteProject()) {
      return;
    }
    if (this.showForm()) {
      this.stopEdit();
      return;
    }
    this.showForm.set(true);
  }

  stopEdit(): void {
    const project = this.project();
    if (project) {
      this.patchForm(project);
    }
    this.showForm.set(false);
  }

  private patchForm(project: ProjectDTO): void {
    this.form.patchValue({
      name: project.name,
      topic: project.topic,
    }, {emitEvent: false});
  }

  changeExplore(exploringMode: boolean): void {
    const project = this.project();
    if (project) {
      const toUpdate: ProjectDTO = {...project, exploringMode};
      this.store.dispatch(updateProject({id: project.id, project: toUpdate}));
    }
  }

  navigateToNextStep(): void {
    const currentUrl = this.router.url;
    if (currentUrl.endsWith('/setting')) {
      this.router.navigate(['../search-setting'], {relativeTo: this.route});
    } else {
      this.router.navigate(['search-setting'], {relativeTo: this.route});
    }
  }
}
